export const RISK_COLOURS = {
  low:         { bg: '#EAF3DE', color: '#27500A', line: '#3B6D11' },
  average:     { bg: '#FAEEDA', color: '#633806', line: '#854F0B' },
  medium:      { bg: '#FAEEDA', color: '#633806', line: '#854F0B' },
  high:        { bg: '#FAECE7', color: '#993C1D', line: '#993C1D' },
  significant: { bg: '#FCEBEB', color: '#791F1F', line: '#A32D2D' },
  critical:    { bg: '#FCEBEB', color: '#791F1F', line: '#A32D2D' },
  unknown:     { bg: '#f2f2f0', color: '#5f5e5a', line: '#9a9a9a' },
};

// color mapping shared by ReportStatGrid, ReportSection badges, etc.
export const TONE_COLOURS = {
  neutral: { bg: '#f2f2f0', color: '#5f5e5a', line: '#14243A' },
  success: { bg: '#EAF3DE', color: '#27500A', line: '#3B6D11' },
  warning: { bg: '#FAEEDA', color: '#633806', line: '#854F0B' },
  danger:  { bg: '#FCEBEB', color: '#791F1F', line: '#A32D2D' },
};

export default {
  methods: {
    formatDate(dateString) {
      if (!dateString) return '—';
      return new Date(dateString).toLocaleDateString();
    },
    formatDateTime(date) {
      if (!date) return '—';
      return new Date(date).toLocaleString();
    },
    numberWithCommas(x) {
      if (x === null || x === undefined || isNaN(x)) return '0';
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    capitalizeName(name) {
      if (!name) return '';
      return name.replace(/\b\w/g, l => l.toUpperCase());
    },
    toTitleCase(str) {
      if (!str || typeof str !== 'string') return str ?? '';
      const minor = ['of', 'the', 'and', 'in', 'on', 'at', 'to', 'for', 'a', 'an'];
      return str
        .toLowerCase()
        .replace(/[,\s]+/g, ' ')
        .trim()
        .split(' ')
        .map((word, i) => {
          if (!word) return '';
          if (i > 0 && minor.includes(word)) return word;
          return word.charAt(0).toUpperCase() + word.slice(1);
        })
        .join(' ');
    },
    shortRef(id) {
      if (!id) return '—';
      return id.split('-')[0].toUpperCase();
    },
    isOverdue(dueDate) {
      if (!dueDate) return false;
      return new Date(dueDate) < new Date();
    },
    isExpired(dateString) {
      if (!dateString) return false;
      return new Date(dateString) < new Date();
    },
    hasAddress(addr) {
      if (!addr) return false;
      return !!(addr.street || addr.city || addr.province || addr.country || addr.zipCode);
    },

    // ── Safety ──
    escapeHtml(str) {
      if (str === null || str === undefined) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },

    // ── Tone / pill styling ──
    toneStyle(tone) {
      const t = TONE_COLOURS[tone] || TONE_COLOURS.neutral;
      return `background:${t.bg};color:${t.color}`;
    },
    toneTextColor(tone) {
      return (TONE_COLOURS[tone] || TONE_COLOURS.neutral).line;
    },

    riskPillStyle(level) {
      const c = RISK_COLOURS[(level || '').toLowerCase()] || RISK_COLOURS.unknown;
      return `background:${c.bg};color:${c.color}`;
    },
    riskLevelColor(level) {
      return (RISK_COLOURS[(level || '').toLowerCase()] || RISK_COLOURS.unknown).line;
    },

    watchlistPillStyle(status) {
      const s = (status || '').toLowerCase().replace(/_/g, ' ').trim();
      if (s === 'no match')        return 'background:#EAF3DE;color:#27500A';
      if (s.includes('possible') || s.includes('potential')) return 'background:#FAEEDA;color:#633806';
      if (s === 'match')           return 'background:#FCEBEB;color:#791F1F';
      return 'background:#f2f2f0;color:#5f5e5a';
    },

    statusPillStyle(status) {
      const s = (status || '').toUpperCase();
      if (['ACTIVE', 'RESOLVED', 'CLOSED', 'VERIFIED'].includes(s)) return 'background:#EAF3DE;color:#27500A';
      if (['INACTIVE', 'ESCALATED', 'REJECTED'].includes(s))         return 'background:#FCEBEB;color:#791F1F';
      if (['OPEN', 'PENDING'].includes(s))                           return 'background:#FAEEDA;color:#633806';
      if (s === 'IN_PROGRESS')                                        return 'background:#E6F1FB;color:#0C447C';
      return 'background:#f2f2f0;color:#5f5e5a';
    },

    priorityPillStyle(priority) {
      const p = (priority || '').toUpperCase();
      if (p === 'HIGH')   return 'background:#FCEBEB;color:#791F1F';
      if (p === 'MEDIUM') return 'background:#FAEEDA;color:#633806';
      if (p === 'LOW')    return 'background:#EAF3DE;color:#27500A';
      return 'background:#f2f2f0;color:#5f5e5a';
    },

    countBadgeStyle(isSevere) {
      return isSevere ? 'background:#FCEBEB;color:#791F1F' : 'background:#FAEEDA;color:#633806';
    },

    trendChangeColor(change) {
      if (!change) return '#5f5e5a';
      return change > 0 ? '#27500A' : '#993C1D';
    },
    formatTrendValue(name, value) {
      if (value === null || value === undefined) return '—';
      const isCurrency = /amount|volume|value/i.test(name || '');
      return isCurrency ? `$${this.numberWithCommas(value)}` : this.numberWithCommas(value);
    },
  },
};